import { useState } from "react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Eye, EyeOff, Loader2, X } from "lucide-react";
import api from "../utils/axios";

// ── Safe device fingerprint — no btoa, no Latin-1 issues ─────────────────────
// Matches the same djb2-based hash used on the backend for consistency.
function generateDeviceFingerprint(email = "") {
  const raw = navigator.userAgent + "|" + email;
  let h1 = 0xdeadbeef, h2 = 0x41c6ce57;
  for (let i = 0; i < raw.length; i++) {
    const ch = raw.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822519) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489917);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822519) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489917);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16).padStart(16, "0");
}

export default function Login() {
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [loading,  setLoading]  = useState(false);

  // Forgot-password flow
  const [showForgot,  setShowForgot]  = useState(false);
  const [step,        setStep]        = useState("email");
  const [otp,         setOtp]         = useState("");
  const [newPass,     setNewPass]     = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [fpUserId,    setFpUserId]    = useState(null);
  const [infoMsg,     setInfoMsg]     = useState("");

  // OTP login flow
  const [showLoginOtp,    setShowLoginOtp]    = useState(false);
  const [loginOtp,        setLoginOtp]        = useState("");
  const [rememberDevice,  setRememberDevice]  = useState(false);
  const [loginOtpUserId,  setLoginOtpUserId]  = useState(null);
  const [loginFingerprint,setLoginFingerprint]= useState("");
  const [verifyingOtp,    setVerifyingOtp]    = useState(false);

  const [showPassword, setShowPassword] = useState(false);

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // ── Login submit ─────────────────────────────────────────────────────────────
  async function handleLogin(e) {
    e.preventDefault(); // ← Always first, before any async

    if (!email && !password) { toast.error("Please enter email and password"); return; }
    if (!email)               { toast.error("Please enter your email address"); return; }
    if (!EMAIL_REGEX.test(email)) { toast.error("Please enter a valid email address"); return; }
    if (!password)            { toast.error("Please enter your password"); return; }

    // Generate fingerprint NOW (tied to the email entered)
    const fingerprint = generateDeviceFingerprint(email);

    try {
      setLoading(true);
      const res = await api.post(
        "/auth/login",
        { email, password, deviceFingerprint: fingerprint },
        { withCredentials: true }
      );

      if (res.data.skipOTP) {
        // Trusted device — go straight in
        localStorage.setItem("user", JSON.stringify(res.data));
        window.location.href = "/";
        return;
      }

      if (res.data.otpRequired) {
        setLoginOtpUserId(res.data.userId);
        setLoginFingerprint(fingerprint);
        setShowLoginOtp(true);
        toast.success("OTP sent to your email");
      }
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Email or password is incorrect");
      } else {
        toast.error("Something went wrong. Please try again.");
        console.error("Login error:", err);
      }
    } finally {
      setLoading(false);
    }
  }

  // ── Forgot password ───────────────────────────────────────────────────────────
  async function handleForgot() {
    if (!email) { toast.error("Enter your Email Address in the login form first"); return; }
    if (!EMAIL_REGEX.test(email)) { toast.error("Please enter a valid email address"); return; }
    try {
      const res = await api.post("/auth/forgot-password", { email });
      if (res.data.type === "client") {
        setFpUserId(res.data.userId);
        setInfoMsg("");
        setStep("otp");
      } else {
        setStep("email");
        setInfoMsg("Your request has been raised. Admin will share the updated password with you.");
      }
      setShowForgot(true);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to process request");
    }
  }

  async function verifyForgotOtp() {
    try {
      await api.post("/auth/verify-forgot-otp", { userId: fpUserId, otp });
      setStep("reset");
    } catch (err) {
      toast.error(err.response?.data?.error || "Invalid or expired OTP");
    }
  }

  async function resetPassword() {
    if (newPass !== confirmPass) { toast.error("Passwords do not match"); return; }
    if (newPass.length < 6)      { toast.error("Password must be at least 6 characters"); return; }
    try {
      await api.post("/auth/reset-password", { userId: fpUserId, password: newPass });
      toast.success("Password updated. Please login.");
      setShowForgot(false);
      setStep("email");
      setOtp(""); setNewPass(""); setConfirmPass("");
    } catch {
      toast.error("Failed to update password");
    }
  }

  // ── OTP verify ────────────────────────────────────────────────────────────────
  async function verifyLoginOtp(e) {
    e.preventDefault(); // ← Always first

    if (!loginOtp.trim()) { toast.error("Please enter the OTP"); return; }

    try {
      setVerifyingOtp(true);
      const res = await api.post(
        "/auth/verify-otp",
        {
          userId:            loginOtpUserId,
          otp:               loginOtp,
          rememberDevice,
          deviceFingerprint: loginFingerprint,
        },
        { withCredentials: true }
      );

      if (res.data.ok) {
        localStorage.setItem("user", JSON.stringify(res.data));
        window.location.href = "/";
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Invalid or expired OTP");
    } finally {
      setVerifyingOtp(false);
    }
  }

  return (
    <div className="min-h-screen px-8 py-8">
      <Navbar loginOnly={true} />

      <div className="flex items-center justify-center min-h-[80vh]">

        {/* ── LOGIN CARD ─────────────────────────────────────────────────── */}
        <form
          onSubmit={handleLogin}
          noValidate
          className="w-[520px] p-8 rounded-2xl neon-left neon-table"
        >
          <h2 className="text-2xl font-semibold mb-6">Login</h2>

          {/* EMAIL */}
          <div className="neon-input-wrap mb-4 text-center">
            <input
              type="email"
              placeholder="Email Id"
              value={email}
              autoComplete="email"
              className="neon-input h-11 rounded-full text-center focus-visible:ring-0 w-full"
              onChange={e => setEmail(e.target.value)}
            />
          </div>

          {/* PASSWORD */}
          <div className="relative neon-input-wrap mb-4 text-center">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              autoComplete="current-password"
              className="neon-input h-11 rounded-full text-center focus-visible:ring-0 w-full pr-12"
              onChange={e => setPassword(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowPassword(s => !s)}
              className="absolute right-5 top-1/2 -translate-y-1/2
                text-black/60 dark:text-white/60
                hover:text-[#06a0c9] dark:hover:text-[#cffa39] transition-colors"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          {/* ACTIONS */}
          <div className="flex items-center justify-center gap-4">
            <button
              type="submit"
              disabled={loading}
              className="px-8 py-2 rounded-xl bg-neon text-black font-medium
                disabled:opacity-60 disabled:cursor-not-allowed
                flex items-center gap-2 transition-opacity"
            >
              {loading && <Loader2 size={15} className="animate-spin" />}
              {loading ? "Logging in…" : "Login"}
            </button>

            <span
              onClick={handleForgot}
              className="text-sm text-black/60 dark:text-white/60
                hover:text-[#06a0c9] dark:hover:text-[#cffa39]
                cursor-pointer transition-colors"
            >
              Forgot Password?
            </span>
          </div>
        </form>
      </div>

      {/* ── FORGOT PASSWORD MODAL ──────────────────────────────────────────── */}
      {showForgot && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="relative w-[380px] p-6 rounded-2xl neon-left neon-table">
            <button
              onClick={() => { setShowForgot(false); setStep("email"); setInfoMsg(""); }}
              className="absolute top-3 right-3 text-white/60 hover:text-white transition-colors"
            >
              <X size={16} />
            </button>

            <h3 className="text-lg font-semibold mb-4 text-center">Forgot Password</h3>

            {infoMsg && (
              <div className="text-sm text-white/70 bg-white/5 p-3 rounded-lg mb-4">
                {infoMsg}
              </div>
            )}

            {step === "otp" && (
              <>
                <input
                  placeholder="Enter OTP"
                  inputMode="numeric"
                  maxLength={6}
                  className="w-full h-11 px-4 mb-4 rounded-full bg-transparent
                    border border-white/10
                    focus:border-[#06a0c9] dark:focus:border-[#cffa39]
                    outline-none text-center tracking-widest font-mono text-lg"
                  onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                />
                <button
                  onClick={verifyForgotOtp}
                  className="w-full py-2 rounded-xl bg-neon text-black font-medium"
                >
                  Verify OTP
                </button>
              </>
            )}

            {step === "reset" && (
              <>
                <input
                  type="password"
                  placeholder="New Password"
                  className="w-full h-11 px-4 mb-3 rounded-full bg-transparent
                    border border-white/10
                    focus:border-[#06a0c9] dark:focus:border-[#cffa39] outline-none"
                  onChange={e => setNewPass(e.target.value)}
                />
                <input
                  type="password"
                  placeholder="Confirm Password"
                  className="w-full h-11 px-4 mb-4 rounded-full bg-transparent
                    border border-white/10
                    focus:border-[#06a0c9] dark:focus:border-[#cffa39] outline-none"
                  onChange={e => setConfirmPass(e.target.value)}
                />
                <button
                  onClick={resetPassword}
                  className="w-full py-2 rounded-xl bg-neon text-black font-medium"
                >
                  Update Password
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── OTP VERIFICATION MODAL ─────────────────────────────────────────── */}
      {showLoginOtp && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <form
            onSubmit={verifyLoginOtp}
            noValidate
            className="relative w-[380px] p-6 rounded-2xl neon-left neon-table"
          >
            <button
              type="button"
              onClick={() => setShowLoginOtp(false)}
              className="absolute top-3 right-3 text-black/60 dark:text-white/60
                hover:text-black dark:hover:text-white transition-colors"
            >
              <X size={16} />
            </button>

            <h3 className="text-lg font-semibold mb-1 text-center">Login Verification</h3>
            <p className="text-xs text-center text-muted-foreground mb-5">
              A 6-digit code was sent to your email
            </p>

            {/* OTP INPUT */}
            <div className="neon-input-wrap mb-4">
              <input
                placeholder="• • • • • •"
                value={loginOtp}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                className="neon-input h-12 rounded-full text-center
                  tracking-[0.5em] text-xl font-bold
                  focus-visible:ring-0 w-full"
                onChange={e => setLoginOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              />
            </div>

            {/* REMEMBER DEVICE */}
            <label className="flex items-center gap-2.5 text-sm mb-5
              text-black/70 dark:text-white/70 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={rememberDevice}
                onChange={() => setRememberDevice(s => !s)}
                className="w-4 h-4 rounded
                  accent-[#06a0c9] dark:accent-[#cffa39]"
              />
              <span>
                Remember this device for{" "}
                <span className="font-semibold
                  text-[#06a0c9] dark:text-[#cffa39]">
                  7 days
                </span>
              </span>
            </label>

            <button
              type="submit"
              disabled={verifyingOtp || loginOtp.length < 6}
              className="w-full py-2.5 rounded-xl bg-neon text-black font-medium
                disabled:opacity-50 disabled:cursor-not-allowed
                flex items-center justify-center gap-2 transition-opacity"
            >
              {verifyingOtp && <Loader2 size={15} className="animate-spin" />}
              {verifyingOtp ? "Verifying…" : "Verify & Login"}
            </button>
          </form>
        </div>
      )}
      <Footer />
    </div>
  );
}
